import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  LayoutList,
  X,
} from 'lucide-react';
import type { Parcours, ParcoursStep } from '../../types/parcours';
import BlocksRenderer from './BlocksRenderer';
import { getProgress, markStepDone } from '../../lib/progressStore';
import { supabase, safeSupabaseQuery } from '../../lib/supabase';

interface Props {
  parcours: Parcours;
  currentStep: ParcoursStep;
}

export default function CourseLayout({ parcours, currentStep }: Props) {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteLogo, setSiteLogo] = useState<string | null>(null);

  useEffect(() => {
    safeSupabaseQuery(() =>
      supabase.from('site_settings').select('value').eq('key', 'logo').maybeSingle()
    ).then(({ data }) => {
      if (data?.value) setSiteLogo(data.value);
    });
  }, []);

  const steps = parcours.steps ?? [];
  const currentIdx = steps.findIndex((s) => s.step_id === currentStep.step_id);
  const prevStep = currentIdx > 0 ? steps[currentIdx - 1] : null;
  const nextStep = currentIdx < steps.length - 1 ? steps[currentIdx + 1] : null;
  const isLast = !nextStep;

  const isCurrentDone = completedSteps.includes(currentStep.step_id);
  const doneCount = completedSteps.filter((id) => steps.some((s) => s.step_id === id)).length;
  const progressPct = steps.length > 0 ? Math.round((doneCount / steps.length) * 100) : 0;

  useEffect(() => {
    setCompletedSteps(getProgress(parcours.slug));
  }, [parcours.slug, currentStep.step_id]);

  // Scroll to top of content on step change
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep.step_id]);

  function handleMarkDone() {
    markStepDone(parcours.slug, currentStep.step_id);
    setCompletedSteps(getProgress(parcours.slug));
  }

  function handleNext() {
    markStepDone(parcours.slug, currentStep.step_id);
    setCompletedSteps(getProgress(parcours.slug));
    if (nextStep) {
      navigate(`/decouvrir/${parcours.slug}/etape/${nextStep.step_id}`);
    } else {
      navigate(`/decouvrir/${parcours.slug}`);
    }
  }

  function handlePrev() {
    if (prevStep) navigate(`/decouvrir/${parcours.slug}/etape/${prevStep.step_id}`);
  }

  return (
    <div className="flex min-h-screen bg-[#f4f5f7]">

      {/* ── Sidebar overlay mobile ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-950/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-[300px] flex-col bg-white shadow-2xl transition-transform duration-300 md:sticky md:top-0 md:h-screen md:translate-x-0 md:shadow-none md:border-r md:border-neutral-200',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-4">
          <Link
            to={`/decouvrir/${parcours.slug}`}
            className="flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-[#d03484] transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <ChevronLeft className="h-4 w-4 shrink-0" />
            <span className="truncate">{parcours.company_name}</span>
          </Link>
          <button
            className="md:hidden shrink-0 rounded-lg p-1 hover:bg-neutral-100 transition-colors"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fermer le menu"
          >
            <X className="h-4 w-4 text-neutral-500" />
          </button>
        </div>

        {/* Logo + progress */}
        <div className="border-b border-neutral-100 px-5 py-4 space-y-3">
          {siteLogo ? (
            <img
              src={siteLogo}
              alt="recruteurs.io"
              className="h-9 w-auto max-w-[160px] object-contain"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="text-lg font-bold gradient-text">recruteurs.io</span>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-neutral-500">Progression</span>
              <span className="text-xs font-bold text-neutral-800">{progressPct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-neutral-400">
              {doneCount} / {steps.length} étape{steps.length !== 1 ? 's' : ''} terminée{doneCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Step list */}
        <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label="Étapes du parcours">
          <ol className="space-y-0.5">
            {steps.map((step, idx) => {
              const isDone = completedSteps.includes(step.step_id);
              const isCurrent = step.step_id === currentStep.step_id;

              return (
                <li key={step.step_id}>
                  <Link
                    to={`/decouvrir/${parcours.slug}/etape/${step.step_id}`}
                    aria-current={isCurrent ? 'step' : undefined}
                    onClick={() => setSidebarOpen(false)}
                    className={[
                      'group flex items-start gap-3 rounded-xl px-3 py-3 text-sm transition-all duration-150',
                      isCurrent
                        ? 'bg-gradient-to-r from-[#d03484]/10 to-[#d47254]/10 text-[#d03484] font-semibold'
                        : isDone
                        ? 'text-neutral-400 hover:bg-neutral-50'
                        : 'text-neutral-700 hover:bg-neutral-50',
                    ].join(' ')}
                  >
                    {/* Icon */}
                    <span className="mt-0.5 shrink-0">
                      {isDone ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      ) : isCurrent ? (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] text-[10px] font-bold text-white">
                          {idx + 1}
                        </span>
                      ) : (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-neutral-200 text-[10px] font-semibold text-neutral-400">
                          {idx + 1}
                        </span>
                      )}
                    </span>

                    {/* Label */}
                    <span className="flex-1 leading-snug">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider block mb-0.5 ${isCurrent ? 'text-[#d03484]/70' : 'text-neutral-400'}`}>
                        Étape {idx + 1}
                      </span>
                      <span className={`${isDone && !isCurrent ? 'line-through opacity-60' : ''}`}>
                        {step.title}
                      </span>
                    </span>

                    {/* Active indicator */}
                    {isCurrent && (
                      <span className="mt-1 shrink-0 h-2 w-2 rounded-full bg-[#d03484]" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Sidebar footer */}
        <div className="border-t border-neutral-100 px-5 py-3">
          <Link
            to={`/decouvrir/${parcours.slug}`}
            className="flex items-center gap-2 text-xs text-neutral-400 hover:text-neutral-700 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Retour à la présentation
          </Link>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex flex-1 flex-col min-w-0">

        {/* Mobile top bar */}
        <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-neutral-200 bg-white/95 backdrop-blur px-4 py-3 md:hidden">
          <Link
            to={`/decouvrir/${parcours.slug}`}
            className="flex items-center gap-1.5 text-sm font-medium text-neutral-600"
          >
            <ChevronLeft className="h-4 w-4" />
            {parcours.company_name}
          </Link>
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-[#d03484] hover:text-[#d03484] transition-colors"
          >
            <LayoutList className="h-3.5 w-3.5" />
            Sommaire
          </button>
        </div>

        {/* Desktop breadcrumb strip */}
        <div className="hidden md:flex items-center gap-2 border-b border-neutral-200 bg-white px-8 py-3 text-xs text-neutral-400">
          <Link to="/decouvrir" className="hover:text-neutral-700 transition-colors">Parcours</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/decouvrir/${parcours.slug}`} className="hover:text-neutral-700 transition-colors">
            {parcours.company_name}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-neutral-700 font-medium">{currentStep.title}</span>
        </div>

        {/* Scrollable content area */}
        <div ref={contentRef} className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl px-5 py-8 md:px-10 md:py-10">

            {/* Step header */}
            <div className="mb-8">
              <div className="mb-3 flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-gradient-to-r from-[#d03484]/15 to-[#d47254]/15 px-3 py-1 text-xs font-bold text-[#d03484]">
                  Étape {currentIdx + 1} / {steps.length}
                </span>
                {isCurrentDone && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Terminée
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-neutral-950 leading-snug mb-3">
                {currentStep.title}
              </h1>
              {currentStep.description && (
                <p className="text-base text-neutral-500 leading-relaxed max-w-2xl">
                  {currentStep.description}
                </p>
              )}
              {/* Progress bar inline for mobile */}
              <div className="mt-5 flex items-center gap-3 md:hidden">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <span className="text-xs font-semibold text-neutral-500 shrink-0">{progressPct}%</span>
              </div>
            </div>

            {/* Blocks */}
            <div className="rounded-2xl bg-white shadow-sm ring-1 ring-neutral-200 overflow-hidden mb-8">
              <div className="p-6 md:p-9">
                {currentStep.blocks && currentStep.blocks.length > 0 ? (
                  <BlocksRenderer blocks={currentStep.blocks} />
                ) : (
                  <p className="text-sm text-neutral-400 italic">Aucun contenu pour cette étape.</p>
                )}
              </div>
            </div>

            {/* Bottom actions */}
            <div className="rounded-2xl bg-white shadow-sm ring-1 ring-neutral-200 p-5 md:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

                {/* Prev */}
                <div className="min-w-0">
                  {prevStep ? (
                    <button
                      onClick={handlePrev}
                      className="group flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 transition-all max-w-[220px] w-full sm:w-auto"
                    >
                      <ChevronLeft className="h-4 w-4 shrink-0 text-neutral-400 group-hover:text-neutral-700 transition-colors" />
                      <span className="truncate">{prevStep.title}</span>
                    </button>
                  ) : (
                    <div />
                  )}
                </div>

                {/* Mark done + Next */}
                <div className="flex items-center gap-3 sm:ml-auto">
                  {!isCurrentDone && (
                    <button
                      onClick={handleMarkDone}
                      className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-all"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Marquer comme terminée</span>
                      <span className="sm:hidden">Terminée</span>
                    </button>
                  )}

                  {isLast ? (
                    <button
                      onClick={handleNext}
                      className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-md shadow-[#d03484]/20 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                    >
                      Terminer le parcours
                      <CheckCircle2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleNext}
                      className="group flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-md shadow-[#d03484]/20 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                    >
                      <span className="truncate max-w-[140px]">{nextStep!.title}</span>
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    </button>
                  )}
                </div>

              </div>

              {/* Last step completion message */}
              {isLast && isCurrentDone && (
                <div className="mt-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 px-5 py-4 text-center">
                  <CheckCircle2 className="mx-auto mb-2 h-7 w-7 text-emerald-500" />
                  <p className="font-bold text-neutral-900 mb-1">Parcours terminé !</p>
                  <p className="text-sm text-neutral-600 mb-3">
                    Vous avez parcouru toutes les étapes de la découverte {parcours.company_name}.
                  </p>
                  <Link
                    to={`/decouvrir/${parcours.slug}`}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white hover:opacity-90 transition-opacity"
                  >
                    Retour à la présentation
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
